import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Ban, Coins, Download, FileText, Flag, MessageCircle, Printer,
  Search, ShieldAlert, Smile, TrendingUp, UserCheck, Users,
} from "lucide-react";
import { toast } from "sonner";
import { axiosInstance } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { getEventExperience } from "@/config/eventCategories";
import { formatDateMMDDYear } from "@/lib/eventTime";

const PLATFORM_RATE = 0.2;
const PROCESSING_RATE = 0.029;
const PROCESSING_FLAT = 0.3;

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const feeBreakdown = (amount, status = "completed") => {
  const gross = String(status).toLowerCase() === "completed" ? Number(amount || 0) : 0;
  const platform = gross * PLATFORM_RATE;
  const processing = gross > 0 ? gross * PROCESSING_RATE + PROCESSING_FLAT : 0;
  return { gross, platform, processing, net: Math.max(0, gross - platform - processing) };
};

const formatEventDate = (date) => {
  if (!date) return "Date not set";
  return formatDateMMDDYear(date);
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "—";
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? timestamp : date.toLocaleString();
};

const reactionLabel = (type) => ({
  heart: "Heart",
  laugh: "Laugh",
  fire: "Fire",
  clap: "Clap",
  wow: "Wow",
}[type] || String(type || "Reaction").replaceAll("_", " "));

const CreatorReligiousReport = ({ user, onLogout }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("summary");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [banBusy, setBanBusy] = useState("");

  useEffect(() => {
    let active = true;
    const loadReport = async () => {
      if (!eventId) {
        setError("Choose an event from your Creator Dashboard to open its report.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get(`/creator/religious-report/${eventId}`);
        if (active) setReport(response.data);
      } catch (requestError) {
        if (active) {
          setError(requestError.response?.data?.detail || "This event report could not be loaded.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadReport();
    return () => { active = false; };
  }, [eventId]);

  const event = report?.event;
  const experience = getEventExperience(event?.category);
  const tithes = useMemo(() => (report?.tithes || []).map((tithe) => ({
    ...tithe,
    ...feeBreakdown(tithe.amount, tithe.status),
  })), [report]);
  const viewers = report?.viewers || [];
  const reactions = report?.reactions?.by_type || [];
  const engagement = report?.engagement || {};
  const protection = report?.content_protection || {};
  const contentReports = report?.content_reports || [];
  const protectionViewers = viewers.filter((viewer) => (
    Number(viewer.content_protection?.total || 0) > 0 || viewer.is_banned
  ));

  const setViewerBan = async (viewer, shouldBan) => {
    if (!viewer?.user_id || banBusy) return;
    const action = shouldBan ? "ban" : "restore access for";
    if (shouldBan && !window.confirm(`Ban ${viewer.display_name} from this event?`)) return;
    setBanBusy(viewer.user_id);
    try {
      if (shouldBan) {
        await axiosInstance.post(`/events/${event.id}/bans/${viewer.user_id}`);
      } else {
        await axiosInstance.delete(`/events/${event.id}/bans/${viewer.user_id}`);
      }
      setReport((current) => ({
        ...current,
        viewers: (current.viewers || []).map((row) => (
          row.user_id === viewer.user_id ? { ...row, is_banned: shouldBan } : row
        )),
        content_reports: (current.content_reports || []).map((row) => (
          String(row.reporter_id) === String(viewer.user_id)
            ? { ...row, reporter_is_banned: shouldBan }
            : row
        )),
        content_protection: {
          ...(current.content_protection || {}),
          banned_viewers: Math.max(0, Number(current.content_protection?.banned_viewers || 0) + (shouldBan ? 1 : -1)),
        },
      }));
      toast.success(shouldBan
        ? `${viewer.display_name} is banned from this event.`
        : `${viewer.display_name} can access this event again.`);
    } catch (requestError) {
      toast.error(requestError.response?.data?.detail || `Could not ${action} this viewer`);
    } finally {
      setBanBusy("");
    }
  };

  const titheTotals = useMemo(() => tithes.reduce((totals, tithe) => ({
    count: totals.count + (tithe.gross > 0 ? 1 : 0),
    gross: totals.gross + tithe.gross,
    platform: totals.platform + tithe.platform,
    processing: totals.processing + tithe.processing,
    net: totals.net + tithe.net,
  }), { count: 0, gross: 0, platform: 0, processing: 0, net: 0 }), [tithes]);

  const filteredTithes = useMemo(() => tithes.filter((tithe) => {
    const status = String(tithe.status || "completed").toLowerCase();
    if (statusFilter !== "all" && status !== statusFilter) return false;
    return !query || `${tithe.display_name} ${tithe.id}`.toLowerCase().includes(query.toLowerCase());
  }), [query, statusFilter, tithes]);

  const filteredReactions = useMemo(() => reactions.filter((reaction) => (
    !query || `${reaction.reaction_type} ${reaction.emoji}`.toLowerCase().includes(query.toLowerCase())
  )), [query, reactions]);

  const downloadCsv = (filename, headers, rows) => {
    const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const exportTithes = () => downloadCsv(
    `${event.id}_tips.csv`,
    ["Viewer", "Amount", "Date & Time", "Status", "Transaction ID", "Platform Fee", "Processing Fee", "Creator Net"],
    filteredTithes.map((tithe) => [
      tithe.display_name, Number(tithe.amount).toFixed(2), formatTimestamp(tithe.created_at),
      tithe.status || "completed", tithe.id, tithe.platform.toFixed(2),
      tithe.processing.toFixed(2), tithe.net.toFixed(2),
    ]),
  );

  const exportReactions = () => downloadCsv(
    `${event.id}_reactions.csv`,
    ["Reaction Type", "Emoji", "Count"],
    filteredReactions.map((reaction) => [reactionLabel(reaction.reaction_type), reaction.emoji, reaction.count]),
  );

  const exportProtection = () => downloadCsv(
    `${event.id}_content_protection.csv`,
    ["Viewer", "Banned", "Time", "Signal", "Operating System", "Browser", "Action", "Details"],
    protectionViewers.flatMap((viewer) => {
      const events = viewer.content_protection?.events || [];
      if (events.length === 0) {
        return [[viewer.display_name, viewer.is_banned ? "Yes" : "No", "", "", "", "", "", ""]];
      }
      return events.map((item) => [
        viewer.display_name, viewer.is_banned ? "Yes" : "No", formatTimestamp(item.timestamp),
        String(item.capture_type || "signal").replaceAll("_", " "), item.os || "unknown",
        item.browser || "unknown", String(item.action_taken || "logged").replaceAll("_", " "),
        item.details || "",
      ]);
    }),
  );

  const exportStatement = () => downloadCsv(
    `${event.id}_event_report.csv`,
    ["Metric", "Value"],
    [
      ["Event Name", event.title], ["Event ID", event.id], ["Category", event.category],
      ["Date", formatEventDate(event.date)], ["Start", event.start_time], ["End", event.end_time],
      ["Status", event.status], ["Event Viewers", engagement.viewer_count || 0],
      ["Viewer Names", viewers.map((viewer) => viewer.display_name).join("; ") || "None recorded"],
      ["Content Protection Violations", protection.total_violations || 0],
      ["Viewers With Protection Violations", protection.violating_viewers || 0],
      ["Viewers Banned From Event", protection.banned_viewers || 0],
      [experience.chatLabel, engagement.message_count || 0],
      [`${experience.reactionsLabel} Reactions`, engagement.reaction_count || 0],
      [`Total ${experience.tipsLabel}`, titheTotals.gross.toFixed(2)],
      ["Platform Fees", titheTotals.platform.toFixed(2)],
      ["Estimated Processing Fees", titheTotals.processing.toFixed(2)],
      ["Estimated Creator Net", titheTotals.net.toFixed(2)],
    ],
  );

  const Stat = ({ icon: Icon, label, value, tint = "text-blue-400" }) => (
    <div className="bg-gray-900/50 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${tint}`} />
        <span className="text-gray-400 text-xs">{label}</span>
      </div>
      <p className="text-white text-2xl font-bold">{value}</p>
    </div>
  );

  const statusPill = (status) => {
    const value = String(status || "completed").toLowerCase();
    const style = {
      completed: "bg-green-600/20 text-green-400",
      pending: "bg-yellow-600/20 text-yellow-400",
      refunded: "bg-orange-600/20 text-orange-400",
      disputed: "bg-red-600/20 text-red-400",
    }[value] || "bg-gray-600/30 text-gray-300";
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${style}`}>{value}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f0f]">
        <Navbar user={user} onLogout={onLogout} isCreator />
        <div className="pt-32 text-center text-gray-400">Loading this event’s report…</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-[#0f0f0f]">
        <Navbar user={user} onLogout={onLogout} isCreator />
        <div className="pt-32 px-4 max-w-xl mx-auto text-center">
          <h1 className="text-white text-2xl font-bold mb-3">Event Report</h1>
          <p className="text-gray-400 mb-6">{error || "This report is unavailable."}</p>
          <Button onClick={() => navigate("/creator/dashboard")} className="bg-blue-600 hover:bg-blue-700">
            Back to Creator Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f0f]" data-testid="religious-report">
      <Navbar user={user} onLogout={onLogout} isCreator />
      <main className="pt-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-20">
        <button onClick={() => navigate("/creator/dashboard")} className="flex items-center gap-2 text-gray-400 hover:text-white mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <span className="inline-block bg-purple-600/20 border border-purple-500/40 text-purple-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-2">
              {event.category} Event Report
            </span>
            <h1 className="text-white text-3xl md:text-4xl font-black">{event.title}</h1>
            <p className="text-gray-400 text-sm mt-1">
              Event ID <span className="font-mono text-gray-300">{event.id}</span> · {formatEventDate(event.date)} · {event.start_time}–{event.end_time}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => window.print()} className="bg-gray-700 hover:bg-gray-600 text-white text-sm flex items-center gap-2">
              <Printer className="w-4 h-4" /> Print / PDF
            </Button>
            <Button onClick={exportStatement} className="bg-blue-600 hover:bg-blue-700 text-white text-sm flex items-center gap-2">
              <Download className="w-4 h-4" /> Export Report
            </Button>
          </div>
        </div>

        <div className="bg-blue-600/10 border border-blue-500/20 text-blue-200 text-sm rounded-lg px-4 py-3 mb-6">
          This report contains activity from this event only. {experience.reactionsLabel} reactions measure engagement and do not have a monetary value.
        </div>

        <div
          className="flex flex-nowrap gap-2 mb-6 border-b border-white/10 overflow-x-auto overscroll-x-contain"
          style={{ WebkitOverflowScrolling: "touch" }}
          aria-label="Event report sections"
        >
          {[
            { key: "summary", label: "Event Summary" },
            { key: "tithes", label: `${experience.tipsLabel} (${tithes.length})` },
            { key: "reactions", label: `${experience.reactionsLabel} (${engagement.reaction_count || 0})` },
            { key: "reports", label: `Viewer Reports (${contentReports.length})` },
            { key: "protection", label: `Content Protection (${protection.total_violations || 0})` },
          ].map((item) => (
            <button
              key={item.key}
              onClick={() => { setTab(item.key); setQuery(""); setStatusFilter("all"); }}
              className={`shrink-0 px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === item.key ? "border-blue-500 text-white" : "border-transparent text-gray-400 hover:text-white"}`}
              data-testid={`report-tab-${item.key}`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {tab === "summary" && (
          <div className="space-y-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat icon={Users} label="Event Viewers" value={Number(engagement.viewer_count || 0).toLocaleString()} />
              <Stat icon={MessageCircle} label={experience.chatLabel} value={Number(engagement.message_count || 0).toLocaleString()} tint="text-purple-400" />
              <Stat icon={Coins} label={`Total ${experience.tipsLabel}`} value={money(titheTotals.gross)} tint="text-yellow-400" />
              <Stat icon={Smile} label={experience.reactionsLabel} value={Number(engagement.reaction_count || 0).toLocaleString()} tint="text-pink-400" />
            </div>

            <section className="rounded-xl bg-gray-900/50 p-6" data-testid="event-viewer-list">
              <div className="mb-4">
                <h2 className="flex items-center gap-2 font-bold text-white">
                  <Users className="h-4 w-4 text-blue-400" /> Event Viewers
                </h2>
                <p className="mt-1 text-xs text-gray-400">Profile usernames are shown first. A viewer's real name is shown when their username is blank.</p>
              </div>
              {viewers.length > 0 ? (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {viewers.map((viewer) => (
                    <div key={viewer.user_id} className="flex min-w-0 items-center gap-3 rounded-lg bg-black/30 px-3 py-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600/20 font-bold text-blue-300">
                        {String(viewer.display_name || "V").replace(/^@/, "").charAt(0).toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white">{viewer.display_name}</span>
                      <button
                        type="button"
                        onClick={() => setViewerBan(viewer, !viewer.is_banned)}
                        disabled={banBusy === viewer.user_id}
                        className={`shrink-0 rounded-md px-2 py-1 text-xs font-bold transition disabled:opacity-50 ${viewer.is_banned ? "bg-green-600/20 text-green-300 hover:bg-green-600/30" : "bg-red-600/20 text-red-300 hover:bg-red-600/30"}`}
                        data-testid={`event-ban-${viewer.user_id}`}
                      >
                        {viewer.is_banned ? "Unban" : "Ban"}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No identifiable viewers were recorded for this event.</p>
              )}
              {Number(engagement.viewer_count || 0) > viewers.length && (
                <p className="mt-3 text-xs text-gray-500">
                  {(Number(engagement.viewer_count || 0) - viewers.length).toLocaleString()} additional viewer name(s) were unavailable from events recorded before attendee profiles were tracked.
                </p>
              )}
            </section>

            <section className="bg-gray-900/50 rounded-xl p-6">
              <h2 className="text-white font-bold mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-400" /> {experience.tipsLabel} Earnings Breakdown
              </h2>
              <div className="divide-y divide-white/5 text-sm">
                {[
                  [`Completed ${experience.tipSingular.toLowerCase()}s`, `${titheTotals.count} (${money(titheTotals.gross)})`],
                  ["Platform fees", `- ${money(titheTotals.platform)}`],
                  ["Estimated payment-processing fees", `- ${money(titheTotals.processing)}`],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4 py-3">
                    <span className="text-gray-400">{label}</span>
                    <span className="text-white font-medium text-right">{value}</span>
                  </div>
                ))}
                <div className="flex justify-between gap-4 py-3">
                  <span className="text-white font-bold">Estimated creator net</span>
                  <span className="text-green-400 font-bold text-lg">{money(titheTotals.net)}</span>
                </div>
              </div>
            </section>

            <section className="bg-gray-900/50 rounded-xl p-6">
              <h2 className="text-white font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-purple-400" /> {experience.reactionsLabel} Engagement
              </h2>
              {reactions.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {reactions.map((reaction) => (
                    <div key={`${reaction.reaction_type}-${reaction.emoji}`} className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
                      <span className="text-white text-sm">{reaction.emoji} {reactionLabel(reaction.reaction_type)}</span>
                      <span className="text-purple-300 font-semibold">{reaction.count}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-gray-500 text-sm">No reactions were recorded for this event.</p>}
            </section>
          </div>
        )}

        {tab !== "summary" && tab !== "protection" && (
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2 bg-gray-900/60 border border-white/10 rounded-lg px-3 py-2 flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={tab === "tithes" ? "Search viewer or transaction ID" : "Search reaction type"}
                className="bg-transparent outline-none text-white text-sm w-full placeholder-gray-500"
                data-testid="report-search"
              />
            </div>
            {tab === "tithes" && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-10 rounded-lg bg-gray-900/60 border border-white/10 px-3 text-sm text-white outline-none"
                data-testid="report-status-filter"
              >
                <option value="all">All statuses</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="refunded">Refunded</option>
                <option value="disputed">Disputed</option>
              </select>
            )}
            <Button onClick={tab === "tithes" ? exportTithes : exportReactions} className="bg-blue-600 hover:bg-blue-700 text-white text-sm flex items-center gap-2">
              <Download className="w-4 h-4" /> Export CSV
            </Button>
          </div>
        )}

        {tab === "tithes" && (
          <div className="overflow-x-auto bg-gray-900/40 rounded-xl border border-white/5">
            <table className="w-full text-sm">
              <thead><tr className="text-gray-400 text-left border-b border-white/10">
                {["Viewer", "Amount", "Date & Time", "Status", "Transaction ID", "Platform Fee", "Processing", "Net"].map((heading) => (
                  <th key={heading} className="px-4 py-3 font-semibold whitespace-nowrap">{heading}</th>
                ))}
              </tr></thead>
              <tbody>
                {filteredTithes.map((tithe) => (
                  <tr key={tithe.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3 text-white">{tithe.display_name === "Anonymous" ? "Anonymous" : `@${tithe.display_name}`}</td>
                    <td className="px-4 py-3 text-yellow-400 font-semibold">{money(tithe.amount)}</td>
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{formatTimestamp(tithe.created_at)}</td>
                    <td className="px-4 py-3">{statusPill(tithe.status)}</td>
                    <td className="px-4 py-3 font-mono text-gray-400">{tithe.id}</td>
                    <td className="px-4 py-3 text-gray-300">{money(tithe.platform)}</td>
                    <td className="px-4 py-3 text-gray-300">{money(tithe.processing)}</td>
                    <td className="px-4 py-3 text-green-400 font-semibold">{money(tithe.net)}</td>
                  </tr>
                ))}
                {filteredTithes.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-500">No {experience.tipSingular.toLowerCase()}s match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "reactions" && (
          <div className="overflow-x-auto bg-gray-900/40 rounded-xl border border-white/5">
            <table className="w-full text-sm">
              <thead><tr className="text-gray-400 text-left border-b border-white/10">
                {['Reaction', 'Type', 'Count', 'Share of Event Reactions'].map((heading) => (
                  <th key={heading} className="px-4 py-3 font-semibold whitespace-nowrap">{heading}</th>
                ))}
              </tr></thead>
              <tbody>
                {filteredReactions.map((reaction) => {
                  const total = Number(engagement.reaction_count || 0);
                  const share = total ? (Number(reaction.count) / total) * 100 : 0;
                  return (
                    <tr key={`${reaction.reaction_type}-${reaction.emoji}`} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3 text-2xl">{reaction.emoji}</td>
                      <td className="px-4 py-3 text-white capitalize">{reactionLabel(reaction.reaction_type)}</td>
                      <td className="px-4 py-3 text-purple-300 font-semibold">{reaction.count}</td>
                      <td className="px-4 py-3 text-gray-300">{share.toFixed(1)}%</td>
                    </tr>
                  );
                })}
                {filteredReactions.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-500">No reactions match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "reports" && (
          <div className="space-y-4" data-testid="creator-content-reports">
            <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
              Each row identifies the viewer who reported this event and the reason they selected. Blocking a viewer applies to all of your current and future events.
            </div>
            {contentReports.length > 0 ? contentReports.map((item) => {
              const reporter = item.reporter || {};
              const viewer = {
                user_id: item.reporter_id,
                display_name: reporter.display_name || reporter.name || reporter.email || "Viewer",
                is_banned: Boolean(item.reporter_is_banned),
              };
              return (
                <section key={item.id} className="rounded-xl border border-white/10 bg-gray-900/50 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h2 className="flex items-center gap-2 font-bold text-white">
                        <Flag className="h-4 w-4 text-red-400" /> {viewer.display_name}
                      </h2>
                      {reporter.email && <p className="mt-1 text-xs text-gray-500">{reporter.email}</p>}
                      <p className="mt-3 text-sm font-semibold text-red-200">{item.reason || "Other"}</p>
                      <p className="mt-1 text-xs text-gray-500">Reported {formatTimestamp(item.created_at || item.updated_at)}</p>
                    </div>
                    <Button
                      onClick={() => setViewerBan(viewer, !viewer.is_banned)}
                      disabled={banBusy === viewer.user_id}
                      className={viewer.is_banned ? "bg-green-700 hover:bg-green-600" : "bg-red-700 hover:bg-red-600"}
                      data-testid={`reporter-ban-${viewer.user_id}`}
                    >
                      {viewer.is_banned ? <UserCheck className="mr-2 h-4 w-4" /> : <Ban className="mr-2 h-4 w-4" />}
                      {viewer.is_banned ? "Unblock viewer" : "Block viewer"}
                    </Button>
                  </div>
                </section>
              );
            }) : (
              <div className="rounded-xl bg-gray-900/50 p-10 text-center text-sm text-gray-400">No viewers reported this event.</div>
            )}
          </div>
        )}

        {tab === "protection" && (
          <div className="space-y-4" data-testid="content-protection-report">
            <div className="flex justify-end">
              <Button onClick={exportProtection} className="bg-blue-600 hover:bg-blue-700 text-white text-sm flex items-center gap-2">
                <Download className="h-4 w-4" /> Export Protection CSV
              </Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Stat icon={ShieldAlert} label="Protection Violations" value={Number(protection.total_violations || 0).toLocaleString()} tint="text-amber-400" />
              <Stat icon={Users} label="Viewers Flagged" value={Number(protection.violating_viewers || 0).toLocaleString()} tint="text-red-400" />
              <Stat icon={Ban} label="Viewers Banned" value={Number(protection.banned_viewers || 0).toLocaleString()} tint="text-red-400" />
            </div>
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              These are browser-detectable protection signals such as blocked screenshot shortcuts, print attempts, screen-share calls, and extended tab hiding. Operating-system screenshots and recordings cannot always be detected by a website.
            </div>
            {protectionViewers.length > 0 ? protectionViewers.map((viewer) => {
              const detail = viewer.content_protection || {};
              return (
                <section key={viewer.user_id} className="rounded-xl border border-white/10 bg-gray-900/50 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="font-bold text-white">{viewer.display_name}</h2>
                      <p className="text-xs text-gray-400">{detail.total || 0} protection violation{Number(detail.total || 0) === 1 ? "" : "s"}</p>
                    </div>
                    <Button
                      onClick={() => setViewerBan(viewer, !viewer.is_banned)}
                      disabled={banBusy === viewer.user_id}
                      className={viewer.is_banned ? "bg-green-700 hover:bg-green-600" : "bg-red-700 hover:bg-red-600"}
                    >
                      {viewer.is_banned ? <UserCheck className="mr-2 h-4 w-4" /> : <Ban className="mr-2 h-4 w-4" />}
                      {viewer.is_banned ? "Restore Event Access" : "Ban From Event"}
                    </Button>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                    {[
                      ["Screenshot / print", detail.screenshot_attempts],
                      ["Recording", detail.recording_attempts],
                      ["Screen share", detail.screen_share_attempts],
                      ["Tab hidden", detail.tab_hidden_signals],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-lg bg-black/30 p-3">
                        <p className="text-xs text-gray-400">{label}</p>
                        <p className="mt-1 font-bold text-white">{Number(value || 0)}</p>
                      </div>
                    ))}
                  </div>
                  {(detail.events || []).length > 0 && (
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full text-left text-xs">
                        <thead><tr className="border-b border-white/10 text-gray-400">
                          <th className="px-2 py-2">Time</th><th className="px-2 py-2">Signal</th><th className="px-2 py-2">Device</th><th className="px-2 py-2">Action</th><th className="px-2 py-2">Details</th>
                        </tr></thead>
                        <tbody>{detail.events.map((item) => (
                          <tr key={item.id} className="border-b border-white/5 text-gray-200">
                            <td className="whitespace-nowrap px-2 py-2">{formatTimestamp(item.timestamp)}</td>
                            <td className="px-2 py-2 capitalize">{String(item.capture_type || "signal").replaceAll("_", " ")}</td>
                            <td className="px-2 py-2">{[item.os, item.browser].filter(Boolean).join(" / ") || "Unknown"}</td>
                            <td className="px-2 py-2 capitalize">{String(item.action_taken || "logged").replaceAll("_", " ")}</td>
                            <td className="px-2 py-2 text-gray-400">{item.details || "—"}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  )}
                </section>
              );
            }) : (
              <div className="rounded-xl bg-gray-900/50 p-10 text-center text-sm text-gray-400">No content-protection violations were recorded for this event.</div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default CreatorReligiousReport;
